import type { components } from '@leanin/api-client';
import { formatPersonRole } from '@/lib/person-role';
import type { CommentItemView } from '../comments/index';
import type { FeedPost, FeedPostAuthor } from './types';

type FeedItemView = components['schemas']['FeedItemView'];
type AuthoredPostView = components['schemas']['AuthoredPostView'];
type PostView = components['schemas']['PostView'];
type AuthorSummary = components['schemas']['AuthorSummary'];
type CommentView = components['schemas']['CommentView'];

export function feedPostAuthor(author: AuthorSummary): FeedPostAuthor {
  if (author.kind === 'deleted') return author;
  return {
    kind: 'member',
    id: author.id,
    name: author.displayName,
    role: formatPersonRole(author.jobTitle, author.company),
    photo: author.photoUrl,
  };
}

export function commentViewToItem(c: CommentView): CommentItemView {
  return {
    id: c.id,
    author: feedPostAuthor(c.author),
    body: c.body,
    bodyFormat: c.bodyFormat,
    mentions: c.mentions,
    linkPreviews: c.linkPreviews,
    createdAt: c.createdAt,
    editedAt: c.editedAt,
    reactionCounts: c.reactionCounts,
    viewerReaction: c.viewerReaction,
    replyCount: c.replyCount,
    parentCommentId: c.parentCommentId,
    viewerCanRemove: c.viewerCanRemove,
    removal: c.removal,
  };
}

function buildFeedPost(
  post: PostView,
  formatTime: (iso: string) => string,
  previewComment: CommentView | undefined,
): FeedPost {
  return {
    id: post.id,
    author: feedPostAuthor(post.author),
    time: formatTime(post.createdAt),
    body: post.body,
    bodyFormat: post.bodyFormat,
    mentions: post.mentions ?? [],
    reactionCounts: post.reactionCounts,
    viewerReaction: post.viewerReaction,
    viewerSaved: post.viewerSaved,
    comments: post.commentCount,
    attachedEntities: post.attachedEntities,
    media: post.media as FeedPost['media'],
    linkPreviews: post.linkPreviews,
    poll: post.poll,
    isGlobal: post.isGlobal,
    scopes: post.scopes,
    viewerCan: post.viewerCan,
    removals: post.removals,
    featuredAt: post.featuredAt,
    previewComment:
      previewComment !== undefined ? commentViewToItem(previewComment) : null,
  };
}

export function feedItemToPost(
  item: FeedItemView,
  formatTime: (iso: string) => string,
): FeedPost | null {
  if (item.kind !== 'post') return null;
  // The server ranks the preview (most-reacted top-level comment) and sends
  // at most one; the card shows exactly one.
  const base = buildFeedPost(item.post, formatTime, item.commentPreview[0]);
  return item.rankReason === 'recommended'
    ? { ...base, recommended: { reason: 'Recommended for you' } }
    : base;
}

/** FeedPost for a bare post with no comment preview — the post the viewer
 *  JUST created, rendered at the top of the feed as immediate confirmation
 *  before (and regardless of where) the ranked refetch places it. */
export function postToFeedPost(
  post: PostView,
  formatTime: (iso: string) => string,
): FeedPost {
  return buildFeedPost(post, formatTime, undefined);
}

export function authoredPostToFeedPost(
  item: AuthoredPostView,
  formatTime: (iso: string) => string,
): FeedPost {
  return buildFeedPost(item.post, formatTime, item.commentPreview[0]);
}
